/** 国械智造埋点跳转来源，供短链接页面之间传递。 */
export const GXZZ_TRACKING_CHANNEL = Object.freeze({
	MAIN_HOME: '总站首页-国械智造',
	TOP_SITE_NAV: '顶部站点导航',
	PRODUCT_DETAIL_STORE: '商详页店铺入口',
	SPECIAL_PRODUCT_CARD_TO_SUBSITE: '专区商品卡片',
	SPECIAL_PRODUCT_CARD: '国械智造专区-商品卡片',
	SUBSITE_SEARCH_RESULT: '子站搜索结果页',
	SUBSITE_RIGHT_NAV: '国械智造子站首页-右侧导航',
	SPECIAL_RIGHT_NAV: '国械智造专区首页-右侧导航',
});

/** 国械智造埋点跳转目标类型。 */
export const GXZZ_TRACKING_TARGET = Object.freeze({
	SUBSITE: 'subsite',
	PRODUCT: 'product',
});

/** 国械智造埋点来源在 sessionStorage 中的存储键。 */
const GXZZ_TRACKING_STORAGE_KEY = 'gxzz_tracking_navigation_context';
/** 国械智造埋点来源有效期，避免历史跳转来源被误用。 */
const GXZZ_TRACKING_CONTEXT_TTL = 5 * 60 * 1000;

/**
 * 生成国械智造商品详情埋点目标标识，避免不同子站商品互相串来源。
 * @param {string|number} companyId 子站 id
 * @param {string|number} productId 商品 id
 * @return {string} 商品详情埋点目标标识
 */
export const buildGxzzProductTrackingTargetId = (companyId, productId) => {
	if (companyId == null || companyId === '' || productId == null || productId === '') return '';
	return `${String(companyId)}:${String(productId)}`;
};

/**
 * 在跳转前保存国械智造埋点来源，避免把来源参数拼到短链接上。
 * @param {string} targetType 跳转目标类型
 * @param {string|number} targetId 跳转目标唯一标识
 * @param {string} channelType 进入渠道
 */
export const setGxzzTrackingContext = (targetType, targetId, channelType) => {
	if (!import.meta.client || !targetType || targetId == null || targetId === '' || !channelType) return;

	try {
		window.sessionStorage.setItem(
			GXZZ_TRACKING_STORAGE_KEY,
			JSON.stringify({
				targetType,
				targetId: String(targetId),
				channelType,
				createdAt: Date.now(),
			})
		);
	} catch {
		// 浏览器禁用会话存储时不阻断原跳转。
	}
};

/**
 * 根据商品详情短链接保存国械智造商品来源。
 * @param {string} href 商品详情链接
 * @param {string} channelType 进入渠道
 */
export const setGxzzProductTrackingContextByHref = (href, channelType) => {
	if (!import.meta.client || !href || !channelType) return;

	try {
		const targetUrl = new URL(href, window.location.origin);
		if (targetUrl.origin !== window.location.origin) return;

		const targetPath = targetUrl.pathname.replace(/^\/cn(?=\/)/, '');
		const productMatch = targetPath.match(/^\/([^/]+)\/products\/([^/]+)\.html\/?$/i);
		if (!productMatch) return;

		const targetId = buildGxzzProductTrackingTargetId(decodeURIComponent(productMatch[1]), decodeURIComponent(productMatch[2]));
		setGxzzTrackingContext(GXZZ_TRACKING_TARGET.PRODUCT, targetId, channelType);
	} catch {
		// 非法链接继续沿用原跳转逻辑，不阻断页面交互。
	}
};

/**
 * 读取并消费当前目标的国械智造埋点来源，防止刷新后重复上报。
 * @param {string} targetType 当前目标类型
 * @param {string|number} targetId 当前目标唯一标识
 * @return {string} 命中的进入渠道
 */
export const consumeGxzzTrackingContext = (targetType, targetId) => {
	if (!import.meta.client || !targetType || targetId == null || targetId === '') return '';

	try {
		const rawContext = window.sessionStorage.getItem(GXZZ_TRACKING_STORAGE_KEY);
		if (!rawContext) return '';

		const context = JSON.parse(rawContext);
		const isExpired = !context?.createdAt || Date.now() - Number(context.createdAt) > GXZZ_TRACKING_CONTEXT_TTL;
		if (isExpired) {
			window.sessionStorage.removeItem(GXZZ_TRACKING_STORAGE_KEY);
			return '';
		}

		if (context.targetType !== targetType || String(context.targetId) !== String(targetId)) return '';

		window.sessionStorage.removeItem(GXZZ_TRACKING_STORAGE_KEY);
		return String(context.channelType || '');
	} catch {
		return '';
	}
};
